import { VSpacer } from '@/components/DesignSystem';
import { AppHeader } from '@/components/shared/AppHeader';
import { Routes } from "@/constants/Routes";
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import {
  ScheduledNotificationRecipientsPage,
} from '@/pages/Admin/NotificationBuilder/components/ScheduledNotificationRecipientsPage';
import { NotificationBuilder } from '@/pages/Admin/NotificationBuilder/NotificationBuilder';
import { VerifyEmail } from '@/pages/Auth/VerifyEmail';
import { Bookings } from '@/pages/Bookings';
import CustomerDetails from "@/pages/FarmerList/CustomerDetails";
import CustomerList from '@/pages/FarmerList/CustomerList';
import RetailerInsight from '@/pages/Insights/RetailerInsight';
import ProductList from '@/pages/Product/ProductList';
import { Promotions } from '@/pages/Promotions/Promotions';
import { Reports } from '@/pages/Reports/Reports';
import { ReportBasePage } from '@/pages/Reports/ReportBasePage';
import { RetailerOverview } from '@/pages/RetailerOverview/RetailerOverview';
import { RetailerAppFooter } from '@/views/RetailerAppFooter';
import { hasUserPermission, UserPermission, UserType } from '@shared/enums';
import React, { ReactNode } from 'react';
import { Navigate, Route, Routes as AppRoutes } from "react-router-dom";

export const RetailerView = () => {
  const { user, retailer } = useAuthentication();

  const canUserAccessOverview = (
    user?.userType === UserType.RetailerAdmin
    || (user?.userType === UserType.SalesPerson && user.defaultSalesperson)
  );

  const canAccessNotificationBuilder = (
    hasUserPermission(user?.permissions, UserPermission.CanAccessNotificationsBuilder)
  );

  const canAccessReports = (
    hasUserPermission(user?.permissions, UserPermission.CanAccessReports)
    || (user?.userType === UserType.SalesPerson && user.defaultSalesperson)
  );

  const createLayout = (element: ReactNode) => {
    return (
      <>
        <AppHeader />
        <VSpacer size="11" />
        <VSpacer size="3" />
        {element}
        <VSpacer size="3" />
        <VSpacer size="11" />
        <RetailerAppFooter />
      </>
    );
  };

  const createReportLayout = (element: ReactNode) => {
    return (
      <>
        {element}
        <RetailerAppFooter />
      </>
    );
  };

  return (
    <AppRoutes>
      <Route element={createLayout(<CustomerList />)} path={Routes.CUSTOMERS} />
      <Route element={createLayout(<CustomerDetails />)} path={Routes.CUSTOMER_DETAILS} />
      <Route element={createLayout(<Promotions />)} path={Routes.PROMOTIONS} />
      <Route element={createLayout(<ProductList />)} path={Routes.PRODUCTS} />
      {canAccessReports && (
        <Route element={createLayout(<Reports />)} path={Routes.REPORTS} />
      )}
      <Route element={createReportLayout(<ReportBasePage />)} path={Routes.REPORT_PAGE} />
      {canAccessNotificationBuilder && (
        <>
          <Route
            element={createLayout(<NotificationBuilder />)}
            path={Routes.NOTIFICATION_BUILDER}
          />
          <Route
            element={createReportLayout(<ScheduledNotificationRecipientsPage />)}
            path={Routes.SCHEDULED_NOTIFICATION}
          />
        </>
      )}
      <Route element={createLayout(<Bookings />)} path={Routes.ADMIN_BOOKINGS} />
      <Route element={createLayout(<VerifyEmail />)} path={Routes.VERIFY_EMAIL} />
      {canUserAccessOverview && (
        <>
          <Route element={createLayout(<RetailerOverview />)} path={Routes.RETAILER_OVERVIEW} />
          {!!retailer?.isChurnEnabled && (
            <Route
              element={createLayout(<RetailerInsight />)}
              path={Routes.RETAILER_INSIGHTS}
            />
          )}
        </>
      )}
      <Route
        element={
          <Navigate to={canUserAccessOverview ? Routes.RETAILER_OVERVIEW : Routes.CUSTOMERS}/>
        }
        path={"*"}
      />
    </AppRoutes>
  );
};
